import React from 'react';
import { useTheme } from '@mui/material';

class StockChart extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainerRef = React.createRef();
    this.chartInstance = null;
    this.resizeObserver = null;
    this.state = {
      chartLoaded: false,
      closeValue: null,
      flowValue: null,
      mouseX: 0,
      mouseY: 0,
      showLegend: false,
      closeCrosshairY: null,
      flowCrosshairY: null,
      currentDate: null,
    };
  }

  componentDidMount() {
    this.initChart();
    this.setupResizeObserver();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data || prevProps.isFullscreen !== this.props.isFullscreen) {
      this.initChart();
    }
  }

  componentWillUnmount() {
    if (this.chartInstance) {
      this.chartInstance.remove();
      this.chartInstance = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setupResizeObserver = () => {
    if (this.chartContainerRef.current && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (!this.chartInstance || !this.chartContainerRef.current) return;
        
        requestAnimationFrame(() => {
          const width = this.chartContainerRef.current.clientWidth;
          const height = this.chartContainerRef.current.clientHeight;
          
          console.log('🔄 Resize detected:', width, height);
          
          if (width > 0 && height > 0) {
            this.chartInstance.applyOptions({
              width: width,
              height: height,
            });
            
            this.chartInstance.timeScale().fitContent();
          }
        });
      });
      
      this.resizeObserver.observe(this.chartContainerRef.current);
    }
  };

  initChart = async () => {
    try {
      const { data, theme } = this.props;

      if (this.chartContainerRef.current) {
        this.chartContainerRef.current.innerHTML = '';
      }

      if (!this.chartContainerRef.current || !data || data.length === 0) {
        return;
      }

      const containerWidth = this.chartContainerRef.current.clientWidth;
      const containerHeight = this.chartContainerRef.current.clientHeight;

      if (containerWidth === 0 || containerHeight === 0) {
        setTimeout(this.initChart, 100);
        return;
      }

      if (!window.LightweightCharts) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/lightweight-charts@4.1.0/dist/lightweight-charts.standalone.production.js';
        script.async = true;
        script.onload = () => this.initChart();
        document.head.appendChild(script);
        return;
      }
      // background: { type: 'solid', color: theme.palette.background.default },
      const chart = window.LightweightCharts.createChart(this.chartContainerRef.current, {
        width: containerWidth,
        height: containerHeight,
        layout: {
          attributionLogo: false,
          background: { type: 'solid', color: '#111827' },
          textColor: '#ffffff',
          // textColor: theme.palette.text.primary,
        },
        grid: {
          vertLines: { color: '#2B3139' },
          horzLines: { color: '#2B3139' },
        },
        // grid: {
        //   horzLines: { color: theme.palette.divider },
        //   vertLines: { color: theme.palette.divider },
        // },
        crosshair: {
          mode: window.LightweightCharts.CrosshairMode.Normal,
          vertLine: {
            visible: true,
            color: '#7f7f7f',
            width: 2,
            style: window.LightweightCharts.LineStyle.Dashed,
            labelVisible: true,
            labelBackgroundColor: '#2B2B43',
          },
          horzLine: {
            visible: false,
            labelVisible: false,
          },
        },
        timeScale: {
          borderColor: '#374151',
          timeVisible: true,
          secondsVisible: false,
          rightOffset: 1,
          barSpacing: 1,
          minBarSpacing: 1,
          fixLeftEdge: false,
          fixRightEdge: false,
          visible: true,
        },
        leftPriceScale: {
          visible: true,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1,
          },
          drawTicks: false,
          entireTextOnly: true,
          mode: window.LightweightCharts.PriceScaleMode.Normal,
        },
        rightPriceScale: {
          visible: true,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1,
          },
          drawTicks: false,
          entireTextOnly: true,
          mode: window.LightweightCharts.PriceScaleMode.Normal,
        },
      });

      this.chartInstance = chart;

      const closeData = data
        .map((item) => ({
          time: item.date,
          value: parseFloat(item.close),
        }))
        .filter((item) => !isNaN(item.value));

      const flowData = data
        .map((item) => ({
          time: item.date,
          value: parseFloat(item.net_val_accum) / 1000000,
        }))
        .filter((item) => !isNaN(item.value));

      const closeSeries = chart.addLineSeries({
        color: '#ff6b35',
        lineWidth: window.innerWidth < 640 ? 2 : 3,
        priceLineVisible: false,
        lastValueVisible: false,
        priceScaleId: 'left',
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 6,
        crosshairMarkerBorderWidth: 2,
        crosshairMarkerBorderColor: '#ff6b35',
        crosshairMarkerBackgroundColor: '#242837',
        lastPriceAnimation: 0,
      });

      const flowSeries = chart.addLineSeries({
        color: '#00d9a5',
        lineWidth: window.innerWidth < 640 ? 2 : 3,
        priceLineVisible: false,
        lastValueVisible: false,
        priceScaleId: 'right',
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 6,
        crosshairMarkerBorderWidth: 2,
        crosshairMarkerBorderColor: '#00d9a5',
        crosshairMarkerBackgroundColor: '#242837',
        lastPriceAnimation: 0,
      });

      closeSeries.setData(closeData);
      flowSeries.setData(flowData);

      if (closeData.length > 0) {
        this.setState({
          closeValue: closeData[closeData.length - 1].value,
          flowValue: flowData[flowData.length - 1].value,
        });
      }

      chart.subscribeCrosshairMove((param) => {
        if (!param || !param.time) {
          this.setState({
            showLegend: false,
            closeCrosshairY: null,
            flowCrosshairY: null,
            currentDate: null,
          });
          if (closeData.length > 0) {
            this.setState({
              closeValue: closeData[closeData.length - 1].value,
              flowValue: flowData[flowData.length - 1].value,
            });
          }
          return;
        }

        const closePrice = param.seriesData.get(closeSeries);
        const flowPrice = param.seriesData.get(flowSeries);

        let closeCrosshairY = null;
        let flowCrosshairY = null;

        if (closePrice && closePrice.value !== undefined) {
          closeCrosshairY = closeSeries.priceToCoordinate(closePrice.value);
        }

        if (flowPrice && flowPrice.value !== undefined) {
          flowCrosshairY = flowSeries.priceToCoordinate(flowPrice.value);
        }

        let dateString = null;
        if (param.time) {
          let dateObj;

          if (typeof param.time === 'string') {
            dateObj = new Date(param.time);
          } else {
            dateObj = new Date(param.time * 1000);
          }

          if (!isNaN(dateObj.getTime())) {
            dateString = dateObj.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });
          }
        }

        this.setState({
          closeValue: closePrice ? closePrice.value : null,
          flowValue: flowPrice ? flowPrice.value : null,
          mouseX: param.point?.x || 0,
          mouseY: param.point?.y || 0,
          showLegend: true,
          closeCrosshairY,
          flowCrosshairY,
          currentDate: dateString,
        });
      });

      chart.timeScale().fitContent();

      this.setState({ chartLoaded: true });

    } catch (error) {
      console.error('Error initializing chart:', error);
      this.setState({ chartLoaded: false });
    }
  };

  render() {
    const { data, isFullscreen } = this.props;
    const { chartLoaded, closeValue, flowValue, showLegend, closeCrosshairY, flowCrosshairY, currentDate } = this.state;

    const heightClass = isFullscreen
      ? 'h-full'
      : 'h-80 sm:h-96 md:h-[500px] lg:h-[600px] xl:h-[700px]';

    if (!data || data.length === 0) {
      return (
        <div className={`w-full ${heightClass}`}>
          <div className="h-full flex items-center justify-center">
            <div className="text-gray-400 text-2xl">ไม่มีข้อมูล</div>
          </div>
        </div>
      );
    }

    const shouldShowCloseLine = chartLoaded && showLegend && closeCrosshairY !== null;
    const shouldShowFlowLine = chartLoaded && showLegend && flowCrosshairY !== null;
    const shouldShowDate = chartLoaded && showLegend && currentDate;

    return (
      <div className={`w-full ${heightClass} relative`} style={{ zIndex: 0 }}>
        <div className="relative h-full px-4 sm:px-6 md:px-8" style={{ zIndex: 1 }}>

          {/* Chart Container - ต้องมาก่อน */}
          <div ref={this.chartContainerRef} className="w-full h-full" style={{ position: 'relative', zIndex: 1 }} />

          {/* Close Line - เส้นส้ม */}
          {shouldShowCloseLine && (
            <div
              key={`close-line-${closeCrosshairY}`}
              className="absolute pointer-events-none"
              style={{
                top: `${closeCrosshairY}px`,
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: '#ff6b35',
                opacity: 0.5,
                boxShadow: '0 0 12px rgba(255, 107, 53, 0.8)',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Flow Line - เส้นเขียว */}
          {shouldShowFlowLine && (
            <div
              key={`flow-line-${flowCrosshairY}`}
              className="absolute pointer-events-none"
              style={{
                top: `${flowCrosshairY}px`,
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: '#00d9a5',
                opacity: 0.5,
                boxShadow: '0 0 12px rgba(0, 217, 165, 0.8)',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Close Value Label */}
          {shouldShowCloseLine && closeValue !== null && (
            <div
              className="absolute pointer-events-none"
              style={{
                left: '80px',
                top: `${closeCrosshairY - 16}px`,
                zIndex: 20,
                fontSize: '18px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(17, 24, 39, 0.98)',
                padding: '6px 10px',
                borderRadius: '6px',
                backdropFilter: 'blur(8px)',
              }}>
                <span style={{ color: '#ff6b35', fontWeight: '600' }}>Close</span>
                <span style={{ color: '#ffffff', fontWeight: '600' }}>
                  {closeValue.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Flow Value Label */}
          {shouldShowFlowLine && flowValue !== null && (
            <div
              className="absolute pointer-events-none"
              style={{
                right: '80px',
                top: `${flowCrosshairY - 16}px`,
                zIndex: 20,
                fontSize: '18px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(17, 24, 39, 0.98)',
                padding: '6px 10px',
                borderRadius: '6px',

                backdropFilter: 'blur(8px)',
              }}>
                <span style={{ color: '#00d9a5', fontWeight: '600' }}>Flow</span>
                <span style={{ color: '#ffffff', fontWeight: '600' }}>
                  {flowValue.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Date Display */}
          {shouldShowDate && (
            <div
              className="absolute pointer-events-none"
              style={{
                bottom: '45px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
              }}
            >
              <div className="bg-gray-900/95 px-3 py-1.5 rounded shadow-lg border border-gray-700">
                <span className="text-white text-sm font-semibold">
                  {currentDate}
                </span>
              </div>
            </div>
          )}

          {/* Axis Labels */}
          <div className="absolute" style={{
            fontSize: '15px',
            color: '#ff6b35',
            fontWeight: '500',
            transform: 'rotate(-90deg)',
            transformOrigin: 'left center',
            left: '15px',
            top: '50%',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}>
            Close
          </div>
          <div className="absolute" style={{
            fontSize: '15px',
            color: '#00d9a5',
            fontWeight: '500',
            transform: 'rotate(90deg)',
            transformOrigin: 'right center',
            right: '15px',
            top: '50%',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}>
            Net Flow
          </div>

          {/* Loading State */}
          {!chartLoaded && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 200 }}>
              <div className="text-gray-400 text-sm">กำลังโหลดกราฟ...</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default StockChart;